import { MouseEvent, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import classNames from "classnames";
import { Avatar } from "@remo-co/ui-core/src/components/Avatar";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import { useShuffleAttendees } from "modules/shuffleAttendees/hooks/useShuffleAttendees";
import { makeSelectUsersInEvent } from "modules/event/usersInEvent/selectors";
import { setActiveMenu, SideMenus } from "modules/eventButtons/redux";
import LobbyAdmitButton from "../../LobbyAdmitButton";
import { toggleLobbyBanner } from "../../redux/slice";
import { selectWaitingGuests } from "../../redux/selectors";
import { getMessageToShow } from "../../utils/lobbyUtils";
import { useStyles } from "./styles";

export const LobbyBanner = ({
  dark = false,
}: {
  dark?: boolean;
}): JSX.Element | null => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const guests = useSelector(selectWaitingGuests);
  const usersInEvent = useSelector(makeSelectUsersInEvent);
  const { showShuffleAttendeesBanner } = useShuffleAttendees();
  const { t } = useI18n();

  const showLobbyWindow = (e: MouseEvent) => {
    e.stopPropagation();
    dispatch(setActiveMenu(SideMenus.LOBBY));
    dispatch(toggleLobbyBanner(false));
  };

  const closeBanner = (e: MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleLobbyBanner(false));
  };

  useEffect(() => {
    if (!guests || !guests.length) {
      dispatch(toggleLobbyBanner(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests]);

  const newGuests = useMemo(
    () => guests.filter((guest) => usersInEvent.get(guest.userId)),
    [guests, usersInEvent],
  );

  const user = usersInEvent.get(newGuests[0]?.userId);

  if (!user) {
    return null;
  }

  const darkClass = dark ? styles.dark : "";

  if (newGuests && newGuests.length) {
    return (
      <Box
        data-testid="lobby-banner"
        className={`${styles.banner} ${darkClass}`}
        onClick={showLobbyWindow}
      >
        <div className={styles.userPresence}>
          <Avatar
            alt={user.profile?.firstName}
            src={user.profile?.picture}
            data-testid="avatar-pic"
          />
        </div>
        <div className={styles.body}>
          <div className={styles.message}>
            {guests.length === 1 && (
              <Typography variant="body1" className={styles.guestName}>
                {guests[0].name}
              </Typography>
            )}
            <Typography variant="body1">{getMessageToShow(guests)}</Typography>
          </div>
          <Button
            onClick={showLobbyWindow}
            variant="primary"
            color="blue"
            className={classNames(styles.viewButton, {
              [styles.blackButton]: showShuffleAttendeesBanner,
            })}
            data-testid="lobby-view-btn"
            size="sm"
          >
            {t("view")}
          </Button>
          {newGuests.length === 1 && !showShuffleAttendeesBanner && (
            <LobbyAdmitButton guest={newGuests[0]} />
          )}
          {!showShuffleAttendeesBanner && (
            <Tooltip title={t("close.notification") as string}>
              <IconButton
                className={styles.closeButton}
                onClick={closeBanner}
                size="small"
                data-testid="close-button"
              >
                <Close />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Box>
    );
  }

  return null;
};
